import React, { useState } from "react";
import { Carousel } from "./Carousel";
import Dictionary from "../../../utils/Dictionary";
import dictionaryData from "../../../../constants/Data";

const Article = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseOver = (e) => {
    const word = e.target.innerText;
    const definition = dictionaryData[word];

    if (definition) {
      setHoveredWord({ word, definition });
      setPosition({ x: e.clientX, y: e.clientY });
    } else {
      setHoveredWord(null);
    }
  };

  const handleMouseOut = () => {
    setHoveredWord(null);
  };
  return (
    <div>
      {hoveredWord && (
        <Dictionary
          word={hoveredWord.word}
          definition={hoveredWord.definition}
          position={position}
        />
      )}
      <div>
        <h2 id="Problem_h">Horizontal Gene Transfer </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          <span>Horizontal Gene Transfer</span>, sometimes referred to as{" "}
          lateral gene transfer, is the process by which genetic
          material is transferred between different <span>genomes</span> or
          organisms. This transfer of DNA can occur between distinct species,
          including exchanges between <span>prokaryotes</span> and{" "}
          <span>eukaryotes</span>. It can also take place between the three
          DNA-containing <span>organelles</span> within eukaryotic cells: the
          nucleus, <span>mitochondrion</span>, and <span>chloroplast</span>.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Horizontal Gene Transfer is a mechanism by which organisms can acquire
          new genetic information from sources other than their direct
          ancestors. This stands in contrast to{" "}
          <span>vertical gene transfer</span>, which involves the transmission
          of <span>genetic material</span> from parents to offspring during
          reproduction. Horizontal Gene Transfer plays a significant role in the
          evolution of organisms and the diversification of their genetic
          traits.
        </p>
        <p className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}>
          This process could be an issue for our <span>LBP</span> as we want to reduce to a maximum 
          the interaction with the rest of the <span>intestinal flora</span>. The Spac or EC20 <span>plasmids</span> could
          potentially be transferred to other bacteria in the intestinal environment, 
          thereby <span>conferring</span> new functionalities to these cells, which is not our intended 
          outcome. As a result, we developed a solution to preemptively address this potential 
          problem.</p>

        <center>
          <div className="mb-2 mt-5">
            <Carousel></Carousel>
          </div>
        </center>
        <h2 className="mt-20" id="Previous iGEM solutions">
          Previous iGEM solutions
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p
          className="mb-20"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          Despite the importance of this problem, other teams searched for other
          solutions to eliminate the problem with unsuccessful results. The
          previous approach relied on the insertion of a double{" "}
          <span>plasmid</span> having both a gene for a toxin and one for the
          anti-toxin
          <a href="#reference1">
            <sup>1</sup>
          </a>
          . If both plasmids are present the toxin of one plasmid is inhibited
          by the anti-toxin of the second plasmid and inversely on a ratio 1:1
          or 1:2. However, lab experiments revealed the challenges and
          instability of this strategy, ultimately leading to the death of the
          bacteria.
        </p>
        <div class="centered">
          <figure>
            <img
              id="Figure1"
              src="https://static.igem.wiki/teams/4888/wiki/wageningen-ur-hgti-image1.jpg"
              alt="project_description"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 1:</u>{" "}
              </b>
              Schematic of engineered bacteria with double toxin-antitoxin
              system from the 2014 Wageningen iGEM team
            </figcaption>
          </figure>
        </div>
        <br></br>
        <h2 className="mb-5 mt-20" id="Our-solution">
          Our solution
        </h2>
        <h2 id="Playing with the properties of Uracilated DNA">
          Playing with the properties of Uracilated DNA
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          We took a different approach to address the problem compared to other
          teams. Our idea stemmed from our understanding that bacteria possess
          the capability to heavily excise uracil-rich DNA, whether it's their
          own or from a donor bacterium. This led us to explore research
          articles, focusing on genes that could be relevant to our problem. We
          identified two key genes: DUT, responsible for replacing uracil with
          thymine when correctly activated, and UNG, a DNase specific to
          uracilated DNA. This entails that a <span>double knockout</span> of
          these genes should yield the opposite effects: a significant
          incorporation of uracil and a loss of defense against uracilated DNA
          from either within the cell or external sources.
        </p>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          In our research, we discovered a unique strain of E. coli, CJ236,
          which was originally used in the 1980s for the Kunkel method, a
          technique for inserting single-point mutations into a plasmid. Based
          on these findings, we formulated our strategy. Here, we present two
          schemes to address Horizontal Gene Transfer (HGT).
        </p>
        <h2 className="mt-20" id="Modified Kunkel method">
          Modified Kunkel method{" "}
        </h2>
        <hr className="dividergeneral mb-20"></hr>
        <div class="centered">
          <figure>
            <img
              id="Figure2"
              src="https://static.igem.wiki/teams/4888/wiki/hgt/hgt1.png"
              alt="project_description"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 2:</u>{" "}
              </b>
              Schematic of modified Kunkel method and how uracilated genome can
              withstand in a mutated bacteria.
            </figcaption>
          </figure>
        </div>
        <br />
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          By using the CJ236 cells, we can theoretically obtain{" "}
          <span>uracilated plasmid</span> using the following pathway taking
          inspiration from <span>Kunkel's method</span>. First, we transform our
          plasmid to the bacteria which we infect with a{" "}
          <span>bacteriophage</span> called M13. Thanks to M13 the bacteria will
          have a lot of single-strand DNA which is heavily uracilated. We
          recuperate the ssDNA by using either <span>chloroform</span> or{" "}
          <span>phenol</span>. Up to this point, our procedure aligns with
          Kunkel's method, and detailed protocols can be found online and
          previous iGEM teams
          <a href="#reference2">
            <sup>2</sup>
          </a>
          . <br></br>
          From here, we modify it according to our needs. We perform a
          polymerase reaction with <span>dNTPs</span> except dTTP which is
          replaced by <span>dUTP</span>. This way, we obtain a dsDNA that is
          uracilated. Optionally, <span>SYBR green</span> can be used to show
          the correct formation of the double strand. In doing so, we obtain a
          uracilated plasmid. <br></br>
          Then using a tool such as <span>CRISPR-CAS9</span>, inducing a
          mutation on the gene UNG would make the DNase ineffective and the
          engineered bacteria would have no defense against the uracilated
          incoming plasmid.
        </p>
        <h2 className=" mt-20" id="Uracilated plasmid from CJ236 and medium">
          Uracilated plasmid from CJ236 and medium
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p
          className="mb-20"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          This technique is simple and more elegant than the one previously
          described, so we decided to give it a try. <br></br>
          Starting with our CJ236 cells we perform a <span>
            transformation
          </span>{" "}
          with an easy to manipulate <span>plasmid</span> coding for a GFP when{" "}
          <span>induced</span> with <span>Arabinose</span> (pGLO).
        </p>
        <div class="centered">
          <figure>
            <img
              id="Figure1"
              src="https://static.igem.wiki/teams/4888/wiki/hgt/hgt2.png"
              alt="project_description"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 3:</u>{" "}
              </b>
              Schematic of this team pathway to generate Uplasmdid.
            </figcaption>
          </figure>
        </div>
        <br></br>
        <p
          className="mb-20"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          The essential sequences of pGLO include the following
          <a href="#reference3">
            <sup>3</sup>
          </a>
          :<br></br>
          <br></br>
          <ol className="ml-12 mb-10">
            <li>
              <strong>GFP —</strong> sequence originating from the jellyfish and
              codes for the green fluorescent protein (GFP)
            </li>
            <li>
              <strong>bla —</strong> gene that encodes β-lactamase, an enzyme
              that breaks down the antibiotic Ampicillin; transformants
              expressing the bla gene can be selected by placing Ampicillin in
              the growth medium
            </li>
            <li>
              <strong>pBAD promoter —</strong> binds AraC-arabinose and promotes{" "}
              <span>RNA polymerase</span> binding and transcription of the GFP
              gene
            </li>
            <li>
              <strong>araC —</strong> gene that encodes the regulatory protein
              that binds to the pBAD promoter; only when arabinose binds to the
              AraC protein is the production of GFP switched on
            </li>
            <li>
              <strong>Multiple cloning site —</strong> a region containing
              restriction sites (NdeI, HindIII, EcoRI, etc.), sequences that
              permit the insertion or deletion of a gene of interest{" "}
            </li>
          </ol>
          <br></br>
          Bacteria transformed with the pGLO plasmid are selected by{" "}
          <span>Ampicillin resistance</span> and when <span>induced</span> to
          express GFP, they glow fluorescent green under UV light!
        </p>
        <div class="centered">
          <figure>
            <img
              id="Figure1"
              src="https://static.igem.wiki/teams/4888/wiki/pglo.png"
              alt="project_description"
              class="centered-image"
              style={{ width: "90%" }}
            />
            <br></br>
            <figcaption>
              <b>
                <u>Figure 4:</u>{" "}
              </b>
              pGLO map <i> made with Biorender </i>. pGLO map{" "}
              <i> made with Biorender </i>.
            </figcaption>
          </figure>
        </div>
        <br></br>
        <p
          className="mb-2"
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          The <span>transformation</span> of the pGLO into CJ236 will result in
          a gradual incorporation of <span>uracil</span> into the plasmid due to
          the two mutations of the strain. To speed up the incorporation we will
          grow the bacteria colonies into a <span>LB + dUTP solution</span>.
          After checking for different conditions (time of growth + dUTP
          concentration) we will <span>lyse</span> the cells to recuperate our
          uracilated pGLO. To conclude our process, we would make a
          transformation on both CJ236 and another strain as a{" "}
          <span>control</span>. In CJ236, the plasmid is not{" "}
          <span>degraded</span> because the UNG gene is mutated, whereas the
          other strain should degrade the plasmid. By examining the
          fluorescence, we can observe a green signal in CJ236 and no signal in
          the other strain.
        </p>

        <h2 className="mt-20" id="References">
          References
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul className="mb-20">
          <ol type="1">
            <li id="reference1">
              2014 Wageningen iGEM team <br />
              <a href="https://2014.igem.org/Team:Wageningen_UR/project/gene_transfer">
                Double toxin-antitoxin system Double toxin-antitoxin system
              </a>
            </li>

            <li id="reference2">
              2012 Washington iGEM team <br />
              <a href="https://2012.igem.org/Team:Washington/Protocols/Kunkel">
                Kunkel protocol Kunkel protocol
              </a>
            </li>

            <li id="reference3">
              pGLO plasmid <br />
              <a href="https://www.bio-rad.com/en-ch/applications-technologies/pglo-plasmid-map-resources?ID=NISQOC15">
                Bio-Rad's Bio-Rad's
              </a>
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
